import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { getMyCompany } from '../services/companyService';
import MaSociete from '../components/MaSociete';
import { stockQuotaFromMyCompany } from '../utils/quotas';
import { useLanguage } from '../contexts/LanguageContext';
import EnteteEcran from '../components/commun/EnteteEcran';
import DialogueConfirmation from '../components/commun/DialogueConfirmation';
import { isPrivilegedRole } from '../utils/permissions';

const Admin = ({ showSnackbar }) => {
  const { t } = useLanguage();
  // Onglet d'arrivée, quand on vient d'un renvoi (le profil pointe « Ma
  // société », lot 6e-iv). Sans cela le renvoi déposerait l'utilisateur sur
  // « Utilisateurs » en le laissant chercher l'onglet qu'on venait de lui
  // nommer. ⚠️ Lu une seule fois, à l'initialisation : `location.state`
  // survit à un rechargement de page, et le relire à chaque rendu rendrait
  // impossible de changer d'onglet ensuite.
  const location = useLocation();
  const [tabValue, setTabValue] = useState(location.state?.tab ?? 0);
  const [users, setUsers] = useState([]);
  const [stocks, setStocks] = useState([]);
  // Quotas de l'entreprise (UI-3) : { company, usage } ou null (échec d'appel / pas d'entreprise).
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingStock, setEditingStock] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    // ⚠️ Pas de clé `company_name` non plus, MÊME MOTIF que `address` ci-dessous
    // (lot 6d-iv). La raison sociale vient de `Company.name`, saisie dans
    // l'onglet « Ma société » ; elle ne se dérive plus d'un champ recopié dans
    // chaque compte, où N utilisateurs pouvaient la faire diverger.
    // ⚠️ Pas de clé `address` ici, et c'est DÉLIBÉRÉ. `formData` est envoyé
    // ENTIER à /auth/register et /auth/users/<id> (l. ~204-207). Laisser un
    // `address: ''` résiduel aurait fait ÉCRASER par une chaîne vide toutes les
    // adresses déjà saisies, à chaque enregistrement — une perte de données
    // silencieuse en retirant un champ. Absente de l'objet, la clé n'est pas
    // envoyée, et `update_user` (`if 'address' in data`) ne touche pas la
    // colonne : les valeurs existantes sont préservées telles quelles.
    role: 'USER',
    stock_ids: [],
    is_active: true,
    can_delete_products: false,
    can_delete_orders: false,
    can_delete_suppliers: false,
  });
  const [stockFormData, setStockFormData] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    email: '',
  });

  // Déclarer loadData avant le useEffect qui l'utilise
  // `showSnackbar` est MÉMOÏSÉ par useCallback dans App.js : son identité est stable
  // entre les rendus, l'ajouter aux deps ne provoquerait AUCUNE boucle. Il est hors deps
  // par simple économie, comme `t` ci-dessous — pas pour une raison de sûreté.
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersResponse, stocksResponse, companyData] = await Promise.all([
        apiService.get('/auth/users'),
        apiService.get('/admin/stocks'),
        // Chemin décoratif : un échec ne doit pas interrompre le chargement de la page.
        // On absorbe l'erreur ici (console seulement) -> le bandeau reste simplement masqué.
        getMyCompany().catch((error) => {
          console.error('Quotas indisponibles:', error);
          return null;
        }),
      ]);

      setUsers(usersResponse.data.users);
      setStocks(stocksResponse.data.stocks);
      setCompanyInfo(companyData);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      showSnackbar(t('admin.loadError'), 'error');
    } finally {
      setLoading(false);
    }
    // `t` volontairement hors deps. Il n'est pas mémoïsé, mais son identité ne change QUE
    // lorsque LanguageProvider se re-rend — or il est monté au-dessus d'AppContent, donc
    // les rendus de cette page ne l'affectent pas (pas de boucle : Profile.js a `t` dans
    // ses deps et ne déclenche qu'un seul fetch). L'effet réel d'un ajout serait un
    // rechargement des données à chaque changement de langue : inutile, d'où l'exclusion.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Vérifier que l'utilisateur est admin
    const user = localStorage.getItem('user');
    if (!user) {
      showSnackbar(t('admin.accessDenied'), 'error');
      window.location.href = '/';
      return;
    }
    
    const userData = JSON.parse(user);
    if (userData.role !== 'ADMIN') {
      showSnackbar(t('admin.adminOnly'), 'error');
      window.location.href = '/';
      return;
    }

    loadData();
    // `t` hors deps : cf. commentaire sur loadData (éviter un rechargement à chaque
    // changement de langue).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadData, showSnackbar]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleUserDialogOpen = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role,
        stock_ids: user.stocks.map(s => s.id),
        is_active: user.is_active !== false,
        can_delete_products: Boolean(user.can_delete_products),
        can_delete_orders: Boolean(user.can_delete_orders),
        can_delete_suppliers: Boolean(user.can_delete_suppliers),
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'USER',
        stock_ids: [],
        is_active: true,
        can_delete_products: false,
        can_delete_orders: false,
        can_delete_suppliers: false,
      });
    }
    setUserDialogOpen(true);
  };

  const handleStockDialogOpen = (stock = null) => {
    if (stock) {
      setEditingStock(stock);
      setStockFormData({
        name: stock.name,
        description: stock.description || '',
        address: stock.address || '',
        phone: stock.phone || '',
        email: stock.email || '',
      });
    } else {
      setEditingStock(null);
      setStockFormData({
        name: '',
        description: '',
        address: '',
        phone: '',
        email: '',
      });
    }
    setStockDialogOpen(true);
  };

  const handleUserSubmit = async () => {
    try {
      if (editingUser) {
        await apiService.put(`/auth/users/${editingUser.id}`, formData);
        showSnackbar(t('admin.userUpdated'), 'success');
      } else {
        await apiService.post('/auth/register', formData);
        showSnackbar(t('admin.userCreated'), 'success');
      }
      setUserDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Erreur lors de l\'opération:', error);
      showSnackbar(error.response?.data?.error || t('admin.operationError'), 'error');
    }
  };

  const handleStockSubmit = async () => {
    try {
      if (editingStock) {
        await apiService.put(`/admin/stocks/${editingStock.id}`, stockFormData);
        showSnackbar(t('admin.stockUpdated'), 'success');
      } else {
        await apiService.post('/admin/stocks', stockFormData);
        showSnackbar(t('admin.stockCreated'), 'success');
      }
      setStockDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Erreur lors de l\'opération:', error);
      showSnackbar(error.response?.data?.error || t('admin.operationError'), 'error');
    }
  };

  /**
   * 🔴 LES DEUX SUPPRESSIONS PASSENT PAR UNE ANNONCE, PLUS PAR `window.confirm`.
   * *« Supprimer ce stock » n'apprend rien ; « ce stock porte 12 produits et
   * 4 commandes » décide.* Un dialogue natif ne sait offrir qu'OK et Annuler —
   * il ne peut RIEN montrer.
   * ⚠️ `aSupprimer` porte l'ENTITÉ, pas seulement son identifiant : l'annonce a
   * besoin de son nom et de ses compteurs pour dire ce qu'elle emporte.
   */
  const [aSupprimer, setASupprimer] = useState(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);

  const confirmerSuppression = async () => {
    if (!aSupprimer) return;
    setSuppressionEnCours(true);
    const { type, entite } = aSupprimer;
    try {
      await apiService.delete(type === 'user'
        ? `/auth/users/${entite.id}` : `/admin/stocks/${entite.id}`);
      showSnackbar(t(type === 'user' ? 'admin.userDeleted' : 'admin.stockDeleted'),
                   'success');
      setASupprimer(null);
      loadData();
    } catch (error) {
      // ⚠️ LE MESSAGE DU SERVEUR L'EMPORTE : il porte le refus MÉTIER — « ce
      // stock contient des produits », « dernier stock de l'entreprise ». Le
      // remplacer par un texte générique effacerait la seule information utile.
      setASupprimer(null);
      showSnackbar(error.response?.data?.error || t('admin.deleteError'), 'error');
    } finally {
      setSuppressionEnCours(false);
    }
  };

  // handleLoginAsUser a déménagé dans la console super-admin (SuperAdminConsole.js, UI-6).
  // Il était INATTEIGNABLE ici : le SUPER_ADMIN n'accède pas à /admin (D-UI-2, appliqué
  // deux fois — Sidebar teste role === 'ADMIN' en égalité stricte, et cette page rejette
  // tout autre rôle). L'impersonation part désormais de l'écran où le SA se trouve
  // réellement.

  // Calcul délégué à utils/quotas.js — source de vérité unique, partagée avec la console
  // super-admin (UI-4). Le savoir y vit : max_stocks est DÉJÀ la limite effective
  // (override ?? plan) résolue côté serveur, jamais à recalculer ici.
  // null => bandeau masqué : appel échoué, ou utilisateur sans entreprise (SUPER_ADMIN).
  // `plan` n'est pas un quota : il reste lu directement, le helper ne le porte pas.
  const quota = stockQuotaFromMyCompany(companyInfo);
  const stocksQuota = quota ? { ...quota, plan: companyInfo.company.plan } : null;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* 🔴 `EnteteEcran`, COMME LES SEPT AUTRES ÉCRANS DE TRAVAIL — Admin
          portait encore un `<Typography variant="h4">` nu. Il ne restait que
          lui et Notifications ; la dette est soldée pour celui-ci. */}
      <EnteteEcran titre={t('admin.title')} />

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={t('admin.users')} />
          <Tab label={t('admin.stocks')} />
          {/* ⚠️ L'onglet n'apparaît QUE si l'appelant a une entreprise. Le
              SUPER_ADMIN n'en a pas (company_id NULL) : `getMyCompany` lui rend
              `company: null`, et les trois routes de la fiche lui rendraient
              400. Afficher l'onglet lui promettrait un écran qui refuse tout.
              Il édite les entreprises depuis sa console. */}
          {companyInfo?.company && <Tab label={t('company.tab')} />}
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">{t('admin.userManagement')}</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleUserDialogOpen()}
            >
              {t('admin.newUser')}
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.name')}</TableCell>
                  <TableCell>{t('admin.email')}</TableCell>
                  <TableCell>{t('admin.role')}</TableCell>
                  <TableCell>{t('admin.stocks')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                  <TableCell>{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.full_name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {/* 🔴 LE LIBELLÉ EST TRADUIT, il ne l'était pas. La cellule
                          rendait `user.role` BRUT — un lecteur hébreu ou anglais
                          lisait « ADMIN ». La console super-admin, elle,
                          traduisait déjà : *deux écrans affichaient la même
                          donnée dans deux langues.*
                          ⚠️ Trouvé par une GARDE, pas par la lecture : elle
                          cherchait la clé de traduction et ne l'a pas trouvée.
                          ⚠️ Et un commentaire JSX ne se place PAS dans une liste
                          d'attributs — la première écriture l'y avait mis, et le
                          build a refusé. */}
                      <Chip
                        label={user.role === 'ADMIN'
                          ? t('admin.roleAdmin') : t('admin.roleUser')}
                        color={user.role === 'ADMIN' ? 'error' : 'primary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user.stocks.map(stock => (
                        <Chip
                          key={stock.id}
                          label={stock.name}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? t('common.active') : t('common.inactive')}
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleUserDialogOpen(user)}
                        title={t('common.edit')}
                      >
                        <EditIcon />
                      </IconButton>
                      {/* Garde par RÔLE, alignée sur delete_user côté serveur.
                          L'email en dur ne masquait le bouton que pour UN compte,
                          et il est modifiable depuis cet écran même. */}
                      {user.role !== 'SUPER_ADMIN' && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setASupprimer({ type: 'user', entite: user })}
                          title={t('common.delete')}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tabValue === 1 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">{t('admin.stockManagement')}</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleStockDialogOpen()}
            >
              {t('admin.newStock')}
            </Button>
          </Box>

          {stocksQuota && (
            <Alert severity={stocksQuota.reached ? 'warning' : 'info'} sx={{ mb: 2 }}>
              {t('admin.stocksQuota', { current: stocksQuota.current, limit: stocksQuota.limit })}
              {' '}
              {t('admin.planLabel', { plan: stocksQuota.plan })}
              {stocksQuota.reached && ` — ${t('admin.quotaReached')}`}
            </Alert>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.name')}</TableCell>
                  <TableCell>{t('admin.description')}</TableCell>
                  <TableCell>{t('admin.address')}</TableCell>
                  <TableCell>{t('admin.users')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                  <TableCell>{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stocks.map((stock) => (
                  <TableRow key={stock.id}>
                    <TableCell>{stock.name}</TableCell>
                    <TableCell>{stock.description}</TableCell>
                    <TableCell>{stock.address}</TableCell>
                    <TableCell>
                      {stock.users.map(user => (
                        <Chip
                          key={user.id}
                          label={user.full_name}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={stock.is_active ? t('common.active') : t('common.inactive')}
                        color={stock.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleStockDialogOpen(stock)}
                        title={t('common.edit')}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setASupprimer({ type: 'stock', entite: stock })}
                        title={t('common.delete')}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ⚠️ La garde répète `companyInfo?.company` plutôt que de se fier au seul
          index d'onglet : sans entreprise, l'onglet n'est pas rendu, donc
          `tabValue` ne peut pas valoir 2 par un clic — mais il le pourrait par
          un état résiduel si l'appel à `getMyCompany` échouait après coup. Le
          composant recevrait alors `company` undefined et poserait un PUT sur
          un formulaire vide. */}
      {tabValue === 2 && companyInfo?.company && (
        <MaSociete
          company={companyInfo.company}
          /* La réponse du PUT ne porte PAS `usage` (aucun des 8 champs ne
             touche aux compteurs) : on remplace la seule clé `company` et on
             conserve l'usage déjà chargé, sinon le bandeau de quotas
             disparaîtrait au premier enregistrement. */
          onCompanyChange={(maj) => setCompanyInfo((prec) => ({ ...prec, company: maj }))}
          showSnackbar={showSnackbar}
        />
      )}

      {/* Dialog pour créer/modifier un utilisateur */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? t('admin.editUser') : t('admin.newUser')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('admin.firstName')}
            value={formData.first_name}
            onChange={(e) => setFormData({...formData, first_name: e.target.value})}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label={t('admin.lastName')}
            value={formData.last_name}
            onChange={(e) => setFormData({...formData, last_name: e.target.value})}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label={t('admin.email')}
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            margin="normal"
            required
          />
          {/* ⚠️ LE CHAMP « NOM DE L'ENTREPRISE » A ÉTÉ RETIRÉ ICI (lot 6d-iv),
              et il ne faut pas le remettre. La raison sociale appartient à la
              SOCIÉTÉ (`Company.name`), elle se saisit dans l'onglet « Ma
              société » et se lit partout ailleurs. La recopier dans chaque
              compte permettait à N utilisateurs de la faire diverger sans
              jamais se croiser — deux collègues, deux raisons sociales sur
              leurs bons de commande respectifs.
              Le retrait est allé jusqu'aux ROUTES : `register` et
              `update_user` n'écrivent plus la colonne. Retirer le champ seul
              aurait laissé la divergence atteignable par un appel direct. */}
          {/* ⚠️ LE CHAMP « ADRESSE » A ÉTÉ RETIRÉ ICI (lot 6c), et il ne faut
              pas le remettre. Le modèle métier arrêté dit qu'un UTILISATEUR
              porte identité et accès, PAS d'adresse propre : il hérite du
              contexte de son stock, et c'est l'adresse du STOCK qui figure sur
              les bons de commande.
              La colonne `User.address` existe toujours en base et n'a pas été
              supprimée — mesuré au lot 6c : elle était ÉCRITE ici et dans le
              profil, exposée par `to_dict()`, et RELUE NULLE PART sauf pour
              re-remplir ces deux mêmes formulaires. Aucun affichage, aucun PDF,
              aucun email. On faisait donc saisir une donnée que personne ne
              lisait — le miroir exact de `Stock.phone`/`Stock.email`, qui
              étaient peuplées et jamais lues. */}
          {!editingUser && (
            <TextField
              fullWidth
              label={t('admin.password')}
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              margin="normal"
              required
            />
          )}
          {/* 🔴 LE SÉLECTEUR DE RÔLE A ÉTÉ RETIRÉ — un ADMIN crée des USER, et
              rien d'autre. Le modèle métier arrêté dit qu'**une société a un
              seul ADMIN**, désigné par le SUPER_ADMIN depuis sa console : le
              choix « Administrateur » ne pouvait donc plus aboutir. Depuis le
              lot S-c il rendait un **409** — *un choix qui promet et échoue*.
              ⚠️ ET IL N'EST PAS REMPLACÉ PAR UN CHAMP EN LECTURE. Un champ qui
              ne peut valoir qu'une chose n'informe pas, il occupe — *c'est le
              compteur « Ruptures » de RU7-a : un contrôle déterminé ne se
              documente pas, il se retire.*
              🟢 EN REVANCHE LA COLONNE « RÔLE » DU TABLEAU RESTE : là elle
              distingue l'ADMIN des USER, donc elle apprend quelque chose.
              ⚠️ `formData.role` SURVIT et n'est pas figé à `'USER'` : il vaut
              le rôle de l'utilisateur ÉDITÉ, et gouverne l'affichage des cases
              de permission de suppression (`isPrivilegedRole`). Le retirer
              afficherait à l'ADMIN des réglages sans effet sur lui-même. */}
          <FormControl fullWidth margin="normal">
            <InputLabel>{t('admin.assignedStocks')}</InputLabel>
            <Select
              multiple
              value={formData.stock_ids}
              onChange={(e) => setFormData({...formData, stock_ids: e.target.value})}
            >
              {stocks.map((stock) => (
                <MenuItem key={stock.id} value={stock.id}>
                  {stock.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {editingUser ? (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label={t('admin.accountActive')}
              />

              <Typography variant="subtitle2" sx={{ mt: 2 }}>
                {t('admin.deletePermissions')}
              </Typography>

              {/* Miroir de can_delete côté backend : ADMIN et SUPER_ADMIN passent outre les
                  flags (isPrivilegedRole = source de vérité unique, cf. utils/permissions.js).
                  On masque donc les cases plutôt que d'afficher un réglage sans effet. */}
              {isPrivilegedRole(formData.role) ? (
                <Typography variant="body2" color="text.secondary">
                  {t('admin.adminsCanDeleteAll')}
                </Typography>
              ) : (
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.can_delete_products}
                        onChange={(e) => setFormData({ ...formData, can_delete_products: e.target.checked })}
                      />
                    }
                    label={t('admin.canDeleteProducts')}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.can_delete_orders}
                        onChange={(e) => setFormData({ ...formData, can_delete_orders: e.target.checked })}
                      />
                    }
                    label={t('admin.canDeleteOrders')}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.can_delete_suppliers}
                        onChange={(e) => setFormData({ ...formData, can_delete_suppliers: e.target.checked })}
                      />
                    }
                    label={t('admin.canDeleteSuppliers')}
                  />
                </FormGroup>
              )}
            </Box>
          ) : (
            /* POST /auth/register n'accepte ni les flags ni is_active : ne rien proposer
               ici plutôt qu'offrir un réglage que le backend ignorerait en silence. */
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t('admin.permissionsAfterCreation')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleUserSubmit} variant="contained">
            {editingUser ? t('common.edit') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour créer/modifier un stock */}
      <Dialog open={stockDialogOpen} onClose={() => setStockDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingStock ? t('admin.editStock') : t('admin.newStock')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('admin.stockName')}
            value={stockFormData.name}
            onChange={(e) => setStockFormData({...stockFormData, name: e.target.value})}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label={t('admin.description')}
            value={stockFormData.description}
            onChange={(e) => setStockFormData({...stockFormData, description: e.target.value})}
            margin="normal"
            multiline
            rows={3}
          />
          <TextField
            fullWidth
            label={t('admin.address')}
            value={stockFormData.address}
            onChange={(e) => setStockFormData({...stockFormData, address: e.target.value})}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t('admin.phone')}
            value={stockFormData.phone}
            onChange={(e) => setStockFormData({...stockFormData, phone: e.target.value})}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t('admin.email')}
            type="email"
            value={stockFormData.email}
            onChange={(e) => setStockFormData({...stockFormData, email: e.target.value})}
            margin="normal"
          />
          {/* 🔴 LE SÉLECTEUR DE COULEUR EST RETIRÉ — VESTIGE MESURÉ.
              Le header était coloré PAR STOCK avant RU1 ; depuis, il est blanc
              en clair et gris neutre en sombre — décision arrêtée du chantier,
              et l'accent doit être le SEUL endroit coloré. Le champ n'a plus
              aucun lecteur : mesuré sur tout le dépôt, `Stock.color` n'était lu
              que par ce formulaire et par la pastille du tableau juste à côté,
              qui ne servait qu'à réafficher ce qu'on venait de saisir.
              **Une boucle fermée sur elle-même.**

              ⚠️ *Un champ qu'on peut remplir et qu'on ne revoit jamais promet
              une trace qu'il ne garde pas* — même motif que le commentaire par
              ligne de reçu. ⚠️ **ET C'EST LA SAISIE QUI PART, PAS LA COLONNE** :
              `Stock.color` reste en base avec ses valeurs. *On ne fait pas de
              migration dans un lot d'interface*, et une colonne inerte ne coûte
              rien tant que personne ne la lit.

              ⚠️ Le balayage a porté sur la CLASSE, pas sur ce cas : tous les
              champs servis ET saisissables ont été confrontés à leurs lecteurs.
              `communication_language` (e-mail et PDF), `can_delete_*`
              (permissions) et `description` (tableau d'administration) sont
              LUS — ils restent. **`color` était le seul vestige.** */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStockDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleStockSubmit} variant="contained">
            {editingStock ? t('common.edit') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🔴 L'ANNONCE DIT CE QUI VA DISPARAÎTRE. Pour un stock, elle porte ses
          compteurs — *« ce stock porte 12 produits » décide, « supprimer ce
          stock » n'apprend rien.* Pour un utilisateur, elle nomme la personne
          et rappelle que ses accès partent avec elle. */}
      <DialogueConfirmation
        ouvert={Boolean(aSupprimer)}
        titre={t(aSupprimer?.type === 'stock'
          ? 'admin.deleteStockTitle' : 'admin.deleteUserTitle')}
        question={t(aSupprimer?.type === 'stock'
          ? 'admin.deleteStockQuestion' : 'admin.deleteUserQuestion',
          { name: aSupprimer?.type === 'stock'
            ? (aSupprimer?.entite?.name || '')
            : (aSupprimer?.entite?.full_name || aSupprimer?.entite?.email || '') })}
        /* 🔴 ON N'ANNONCE QUE CE QUI EST SERVI. La première écriture annonçait
            « ce stock porte N produits et N utilisateurs » — mais
            `Stock.to_dict_with_users()` ne sert **aucun compteur de produits** :
            l'annonce aurait affiché **0**, un chiffre plausible et faux.
            *Un libellé qui nomme une chose et compte une autre survit à toutes
            les relectures, parce que le nombre est plausible* — gravé ce matin,
            failli rejouer cet après-midi.
            ⚠️ Les UTILISATEURS, eux, sont servis : on annonce donc ce qu'on
            sait, et le serveur refuse en 400 nommé ce qu'on ne sait pas (« ce
            stock contient des produits »). **Le compteur de produits du stock
            est ancré en dette** ; le jour où il est servi, cette annonce
            l'ajoute. */
        consequences={aSupprimer?.type === 'stock'
          ? t('admin.deleteStockCarries',
              { users: (aSupprimer?.entite?.users || []).length })
          : null}
        avertissement={t('admin.deleteIrreversible')}
        libelleConfirmer={t('common.delete')}
        enCours={suppressionEnCours}
        onConfirmer={confirmerSuppression}
        onFermer={() => setASupprimer(null)}
      />

    </Box>
  );
};

export default Admin;
